import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Add01Icon,
  Loading03Icon,
  Undo02Icon,
  Cancel01Icon,
  GripVerticalIcon
} from '@hugeicons/core-free-icons'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { cn } from '@/lib/utils'

export type ValidationResult<T> = { ok: true; item: T } | { ok: false; error: string }

const defaultKeyExtractor = <T,>(item: T) => String(item)

function SortableListItem({
  id,
  editable,
  isPendingAdd,
  isPendingRemove,
  onRemove,
  onUndoRemove,
  onUndoAdd,
  children
}: {
  id: string
  editable: boolean
  isPendingAdd: boolean
  isPendingRemove: boolean
  onRemove: () => void
  onUndoRemove: () => void
  onUndoAdd: () => void
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  if (!editable) {
    return <div className="relative">{children}</div>
  }

  return (
    <div ref={setNodeRef} style={style} className={`relative ${isDragging ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-1 border rounded-lg pl-1 pr-1 py-1">
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <HugeiconsIcon icon={GripVerticalIcon} className="size-4" />
        </button>
        <div
          className={`flex-1 min-w-0 ${isPendingRemove ? 'blur-sm' : ''} ${isPendingAdd ? 'opacity-50' : ''}`}
        >
          {children}
        </div>
        {!isPendingRemove && !isPendingAdd && (
          <Button type="button" variant="ghost-destructive" size="icon" onClick={onRemove}>
            <HugeiconsIcon icon={Cancel01Icon} className="size-4" />
          </Button>
        )}
        {isPendingRemove && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="absolute inset-0 m-auto w-fit h-fit"
            onClick={onUndoRemove}
          >
            <HugeiconsIcon icon={Undo02Icon} className="h-3 w-3 mr-1" />
            {t('Undo')}
          </Button>
        )}
        {isPendingAdd && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute top-1 right-1"
            onClick={onUndoAdd}
          >
            <HugeiconsIcon icon={Cancel01Icon} className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  )
}

export default function EditableList<T>({
  items,
  renderItem,
  validateItem,
  onUpdate,
  placeholder,
  keyExtractor = defaultKeyExtractor,
  loading = false,
  emptyMessage,
  editable = true,
  loadRecommendations,
  recommendationsTitle,
  recommendationsEmptyMessage
}: {
  items: T[]
  renderItem: (item: T) => React.ReactNode
  validateItem: (value: string) => ValidationResult<T>
  onUpdate: (items: T[]) => Promise<void>
  placeholder?: string
  keyExtractor?: (item: T) => string
  loading?: boolean
  emptyMessage?: string
  editable?: boolean
  loadRecommendations?: () => Promise<T[]>
  recommendationsTitle?: string
  recommendationsEmptyMessage?: string
}) {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [additions, setAdditions] = useState<T[]>([])
  const [removals, setRemovals] = useState<T[]>([])
  const [saving, setSaving] = useState(false)
  const [visibleCount, setVisibleCount] = useState(10)
  const bottomRef = useRef<HTMLDivElement>(null)

  const [activeTab, setActiveTab] = useState<'list' | 'recommended'>('list')
  const [recommendations, setRecommendations] = useState<T[]>([])
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)

  const removalKeys = new Set(removals.map(keyExtractor))
  const additionKeys = new Set(additions.map(keyExtractor))

  const effectiveItems = useMemo(() => [...items, ...additions], [items, additions])

  const [ordered, setOrdered] = useState<T[]>(() => effectiveItems)

  useEffect(() => {
    setOrdered((prev) => {
      const kept = prev.filter((item) =>
        effectiveItems.some((ei) => keyExtractor(ei) === keyExtractor(item))
      )
      const keptKeys = new Set(kept.map(keyExtractor))
      const newItems = effectiveItems.filter((item) => !keptKeys.has(keyExtractor(item)))
      const next = [...kept, ...newItems]

      if (
        next.length === prev.length &&
        next.every((item, i) => keyExtractor(item) === keyExtractor(prev[i]))
      ) {
        return prev
      }
      return next
    })
  }, [effectiveItems])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && ordered.length > visibleCount) {
          setVisibleCount((prev) => Math.min(prev + 10, ordered.length))
        }
      },
      { root: null, rootMargin: '10px', threshold: 1 }
    )

    const el = bottomRef.current
    if (el) observer.observe(el)
    return () => {
      if (el) observer.unobserve(el)
    }
  }, [visibleCount, ordered.length])

  function handleAdd() {
    if (!input.trim() || saving || loading) return
    setError(null)
    const result = validateItem(input.trim())
    if (!result.ok) {
      setError(result.error)
    } else {
      if (effectiveItems.some((ei) => keyExtractor(ei) === keyExtractor(result.item))) {
        setError(t('Item already in list'))
        return
      }
      setAdditions((prev) => [result.item, ...prev])
      setInput('')
    }
  }

  function handleAddRecommended(item: T) {
    if (saving || loading) return
    const key = keyExtractor(item)
    if (effectiveItems.some((ei) => keyExtractor(ei) === key)) return
    setAdditions((prev) => [item, ...prev])
  }

  function handleRemove(item: T) {
    if (!editable || saving || loading) return
    const key = keyExtractor(item)
    if (additionKeys.has(key)) {
      setAdditions((prev) => prev.filter((a) => keyExtractor(a) !== key))
    } else if (!removalKeys.has(key)) {
      setRemovals((prev) => [...prev, item])
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setOrdered((prev) => {
      const oldIndex = prev.findIndex((item) => keyExtractor(item) === active.id)
      const newIndex = prev.findIndex((item) => keyExtractor(item) === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  async function handleSave() {
    setSaving(true)
    try {
      const removalKeySet = new Set(removals.map(keyExtractor))
      const itemsToSave = ordered.filter((item) => !removalKeySet.has(keyExtractor(item)))
      await onUpdate(itemsToSave)
      setAdditions([])
      setRemovals([])
      setError(null)
    } catch {
      setError(t('Failed to save changes'))
    } finally {
      setSaving(false)
    }
  }

  const loadRecommendationsRef = useRef(loadRecommendations)
  useEffect(() => {
    loadRecommendationsRef.current = loadRecommendations
  }, [loadRecommendations])

  async function loadAndSetRecommendations() {
    const fn = loadRecommendationsRef.current
    if (!fn) return
    setRecommendationsLoading(true)
    try {
      const recs = await fn()
      setRecommendations(recs)
    } finally {
      setRecommendationsLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'recommended') return
    if (recommendations.length > 0 || recommendationsLoading) return
    loadAndSetRecommendations()
  }, [activeTab])

  const hasChanges =
    additions.length > 0 ||
    removals.length > 0 ||
    ordered.some((item, i) => keyExtractor(item) !== keyExtractor(effectiveItems[i]))

  const visibleItems = ordered.slice(0, visibleCount)

  const presentKeys = useMemo(() => {
    const set = new Set<string>()
    for (const item of effectiveItems) set.add(keyExtractor(item))
    return set
  }, [effectiveItems])

  const visibleRecommendations = useMemo(
    () => recommendations.filter((r) => !presentKeys.has(keyExtractor(r))),
    [recommendations, presentKeys]
  )

  const showRecommendations = !!loadRecommendations && editable

  return (
    <div className="space-y-2">
      {showRecommendations && (
        <div className="flex items-center gap-1 border-b">
          <button
            type="button"
            className={cn(
              'py-1.5 px-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'list'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setActiveTab('list')}
          >
            {t('My list')}
            {(additions.length > 0 || removals.length > 0) && (
              <span className="ml-1.5 text-xs text-muted-foreground">{ordered.length}</span>
            )}
          </button>
          <button
            type="button"
            className={cn(
              'py-1.5 px-3 text-sm font-medium border-b-2 transition-colors',
              activeTab === 'recommended'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setActiveTab('recommended')}
          >
            {recommendationsTitle || t('Recommended')}
          </button>
        </div>
      )}

      {activeTab === 'list' && (
        <>
          {editable && (
            <div className="flex items-center gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={placeholder || ''}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAdd()
                  }
                }}
                disabled={saving || loading}
              />
              <Button
                type="button"
                onClick={handleAdd}
                disabled={saving || loading || !input.trim()}
              >
                {saving ? (
                  <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
                ) : (
                  t('Add')
                )}
              </Button>
            </div>
          )}
          {editable && hasChanges && (
            <Button type="button" onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <HugeiconsIcon icon={Loading03Icon} className="animate-spin mr-2" /> : null}
              {t('Save changes')}
            </Button>
          )}
          {error && <div className="text-sm text-destructive">{error}</div>}
          {ordered.length === 0 && emptyMessage && (
            <div className="text-sm text-muted-foreground">{emptyMessage}</div>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={visibleItems.map((item) => keyExtractor(item))}
              strategy={verticalListSortingStrategy}
            >
              {visibleItems.map((item) => {
                const key = keyExtractor(item)
                const isPendingAdd = additionKeys.has(key)
                const isPendingRemove = removalKeys.has(key)

                return (
                  <SortableListItem
                    key={key}
                    id={key}
                    editable={editable}
                    isPendingAdd={isPendingAdd}
                    isPendingRemove={isPendingRemove}
                    onRemove={() => handleRemove(item)}
                    onUndoRemove={() =>
                      setRemovals((prev) => prev.filter((r) => keyExtractor(r) !== key))
                    }
                    onUndoAdd={() =>
                      setAdditions((prev) => prev.filter((a) => keyExtractor(a) !== key))
                    }
                  >
                    {renderItem(item)}
                  </SortableListItem>
                )
              })}
            </SortableContext>
          </DndContext>
          {ordered.length > visibleCount && <div ref={bottomRef} />}
        </>
      )}

      {activeTab === 'recommended' && showRecommendations && (
        <>
          {recommendationsLoading && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <HugeiconsIcon icon={Loading03Icon} className="animate-spin mr-2" />
              {t('Loading recommendations...')}
            </div>
          )}
          {!recommendationsLoading && visibleRecommendations.length === 0 && (
            <div className="text-sm text-muted-foreground py-3">
              {recommendationsEmptyMessage || t('No recommendations available')}
            </div>
          )}
          {!recommendationsLoading &&
            visibleRecommendations.map((item) => {
              const key = keyExtractor(item)
              return (
                <div key={key} className="flex items-center gap-1 border rounded-lg pl-1 pr-1 py-1">
                  <div className="flex-1 min-w-0">{renderItem(item)}</div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    onClick={() => handleAddRecommended(item)}
                  >
                    <HugeiconsIcon icon={Add01Icon} className="size-4" />
                  </Button>
                </div>
              )
            })}
          {!recommendationsLoading && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={loadAndSetRecommendations}
              disabled={recommendationsLoading}
            >
              {t('Refresh recommendations')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}
